// @ts-nocheck
import { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';

import { FHButton, FCheckbox, FBoxContainer, FHSelect } from '../../../../base';
import { useTreatmentPlanEditable } from '../../hooks/useTreatmentPlanEditable';
import { capitalizeFirstLetter } from '../../../../../helpers';
import { getRangeArray } from '../../../../../utils/common';
import { FORTA_BLUE, FORTA_WHITE } from '../../../../../utils/colors';
import TargetItem from './TargetItem';
import { Themes } from '../../../../../constants';
import { GOAL_DETAILS_FIELD_IDS } from '../common/constants/general';
import { GoalMeasurementDurationUnit, GoalTypes, ThresholdTypes } from './constants';
import PlusIcon from '../../../../svgs/PlusIcon';

import type {
  IBehaviorGoalName,
  ITreatmentPlanGoal,
  SelectValueType,
} from '../../../../../utils/types';
import type { ChangeEvent } from 'react';

interface ShortTermGoalSectionProps {
  goal: ITreatmentPlanGoal;
  goalNames: IBehaviorGoalName[];
  errors: any;
  targetSaving: boolean;
  onUpdateField: (key: string, value: any) => void;
  onAddTarget: () => void;
  onSaveTarget: (index: number, key: string, value: string | number) => void;
  onDeleteTarget: (index: number) => void;
  onUpdateTargetActivation: (index: number, reason: string) => void;
}

function ShortTermGoalSection(props: ShortTermGoalSectionProps) {
  const {
    goal,
    errors,
    targetSaving,
    onUpdateField,
    onAddTarget,
    onSaveTarget,
    onDeleteTarget,
    onUpdateTargetActivation,
  } = props;
  const isTpEditable = useTreatmentPlanEditable();
  const [isEditable, setIsEditable] = useState<boolean>(false);

  useEffect(() => {
    if (!goal) return;

    setIsEditable(isTpEditable && goal.is_active);
  }, [isTpEditable, goal]);

  return (
    <Box
      sx={{
        display: 'flex',
        backgroundColor: FORTA_BLUE.light3,
        p: 2,
        borderRadius: '10px',
        flexDirection: 'column',
        boxShadow: `0px 4px 4px ${FORTA_BLUE.light2}`,
      }}
    >
      <Box sx={{ display: 'flex', flexGrow: 1, alignItems: 'center' }}>
        <Typography fontWeight={'bold'} mr={2}>
          Goal:*
        </Typography>
        <FHSelect
          sx={{ flexGrow: 1 }}
          inputHeight={40}
          colorTheme={Themes.Primary}
          disabled={!isEditable}
          options={[
            {
              value: goal[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.id ?? '',
              label: capitalizeFirstLetter(goal[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.label ?? ''),
            },
          ]}
          value={goal[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.id}
          onUpdate={(value: SelectValueType) =>
            setTimeout(() => {
              onUpdateField(GOAL_DETAILS_FIELD_IDS.GOAL_NAME_ID, value);
            })
          }
          error={errors && errors[GOAL_DETAILS_FIELD_IDS.GOAL_NAME_ID]}
        />
      </Box>
      <Typography fontWeight={'bold'} textAlign="left" my={2}>
        Data collection method:
      </Typography>
      <Box sx={{ display: 'flex' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mr: 3 }}>
          <Typography fontWeight={'bold'} mr={2} textAlign="left">
            Threshold type:*
          </Typography>
          <FHSelect
            sx={{ flexGrow: 1, minWidth: 150 }}
            inputHeight={40}
            colorTheme={Themes.Primary}
            disabled={!isEditable}
            options={Object.values(ThresholdTypes).map((value) => ({ value, label: value }))}
            value={goal?.comparand_type || ThresholdTypes.Percent}
            onUpdate={(_value: SelectValueType) => {}}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography fontWeight={'bold'} mr={2} textAlign="left">
            Threshold:*
          </Typography>
          <FHSelect
            sx={{ flexGrow: 1, minWidth: 150 }}
            inputHeight={40}
            colorTheme={Themes.Primary}
            disabled={!isEditable}
            options={getRangeArray(0, 100).map((num) => ({
              value: num,
              label: num,
            }))}
            value={
              goal.mastery_criteria && 'desired_amount' in goal.mastery_criteria
                ? goal.mastery_criteria.desired_amount
                : ''
            }
            onUpdate={(_value: SelectValueType) => {}}
          />
        </Box>
      </Box>
      <Box sx={{ display: 'flex', mt: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mr: 4 }}>
          <Typography fontWeight={'bold'} mr={2}>
            Number of sessions:*
          </Typography>
          <FHSelect
            sx={{ flexGrow: 1, minWidth: 150 }}
            inputHeight={40}
            colorTheme={Themes.Primary}
            disabled={true}
            options={getRangeArray(1, 100).map((num) => ({
              value: num,
              label: num,
            }))}
            value={
              goal.mastery_criteria && 'practice_duration_count' in goal.mastery_criteria
                ? goal.mastery_criteria.practice_duration_count || ''
                : ''
            }
            onUpdate={(_value: SelectValueType) => {}}
          />
        </Box>
        <FCheckbox
          label="Measured across the entire authorization period"
          colorTheme={Themes.Primary}
          disabled={true}
          checked={
            goal.mastery_criteria &&
            'practice_duration_unit' in goal.mastery_criteria &&
            goal.mastery_criteria.practice_duration_unit === GoalMeasurementDurationUnit['Treatment Plan']
          }
          onChange={(_e: ChangeEvent<HTMLInputElement>) => {}}
        />
      </Box>
      {goal?.targets?.length > 0 && (
        <FBoxContainer
          title="Behaviors"
          colorTheme={Themes.Primary}
          titleVariant={'h4'}
          headerStyle={{
            paddingLeft: '18px',
            paddingRight: '18px',
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
            width: '100%',
            marginLeft: 0,

            '& h4': {
              fontSize: '18px',
            },
          }}
          contentStyle={{
            padding: 0,
          }}
          sx={{
            backgroundColor: FORTA_WHITE,
            mt: 3,
          }}
        >
          {goal.targets.map((target, index) => (
            <TargetItem
              key={index}
              index={index}
              target={target}
              saving={targetSaving}
              goalType={goal.goal_type}
              goalTargets={goal.targets}
              goalActive={goal.is_active}
              onSave={(key, value) => onSaveTarget(index, key, value)}
              onDelete={() => onDeleteTarget(index)}
              onUpdateTargetActivation={(reason: string) => onUpdateTargetActivation(index, reason)}
            />
          ))}
        </FBoxContainer>
      )}
      {isEditable && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <FHButton
            startIcon={<PlusIcon color={FORTA_WHITE} />}
            colorTheme={Themes.Primary}
            sx={{ width: 'fit-content' }}
            onClick={onAddTarget}
            error={errors ? errors['targets'] : false}
          >
            {goal?.goal_type === GoalTypes.BehaviorReduction ? 'Add behavior' : 'Add target'}
          </FHButton>
        </Box>
      )}
    </Box>
  );
}

export default ShortTermGoalSection;
